import defaultBank from '@assets/images/bank-logo/000_default.png';
import bmoBank from '@assets/images/bank-logo/001_BMO.png';
import scotiaBank from '@assets/images/bank-logo/002_Scotiabank.png';
import rbcBank from '@assets/images/bank-logo/003_RBC.jpg';
import tdBank from '@assets/images/bank-logo/004_TD.png';
import nationalBankOfCanada from '@assets/images/bank-logo/006_National_Bank_of_Canada.png';
import cibc from '@assets/images/bank-logo/010_CIBC.png';
import hsbcBank from '@assets/images/bank-logo/016_HSBC_Bank_Canada.jpg';
import canadaWesternTrustBank from '@assets/images/bank-logo/030_Canadian_Western_Trust_Company.jpg';
import LauentianBank from '@assets/images/bank-logo/039_Laurentian_Bank_Of_Canada.jpg';
import atbBank from '@assets/images/bank-logo/219_ATB_Financial.jpg';
import manulifeBank from '@assets/images/bank-logo/540_Manulife_Bank_of_Canada.png';
import tangerineBank from '@assets/images/bank-logo/612_Tangerine.png';
import eqLifeBank from '@assets/images/bank-logo/623_EQ_Bank.png';
import flinksBank from '@assets/images/bank-logo/777_Flinks.png';
import coastCapitalBank from '@assets/images/bank-logo/809_Coast_Capital.png';
import desjardinsBank from '@assets/images/bank-logo/815_Desjardins.png';
import meridianBank from '@assets/images/bank-logo/837_Meridian.png';
import type { BankAccount } from '@interfaces/bankAccount';

import { Card, CardContent } from '@ovComponents/2-component/card/card';

import { Menu } from '@ovComponents/2-component/menu/menu';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { Avatar, Box, Grid, IconButton, Typography } from 'hephaestus/web';
import { Delete, Edit, MoreVert } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const BankAccountCard = ({
  bankAccount,
  onEdit,
  onRemove,
  options,
}: {
  bankAccount: BankAccount;
  onEdit: (newName: string) => void;
  onRemove: () => void;
  options: any;
}) => {
  const { t } = useTranslation(['client', 'workflowCompletions', 'components']);
  const [showDeleteBankAccount, setShowDeleteBankAccount] = useState(false);
  const [showEditBankAccount, setShowEditBankAccount] = useState(false);
  const [bankAccountName, setBankAccountName] = useState(
    bankAccount?.name ?? '',
  );
  const [bankAccountNameError, setBankAccountNameError] = useState<{
    hasError: boolean;
    message: string;
  }>({ hasError: false, message: '' });
  const [contextMenuAnchorEl, setContextMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const isBankAccountNameEmpty = bankAccountName.trim() === '';

  const validateBankAccountName = (value: string) => {
    if (value.trim() === '') {
      const errorMessage = t(
        'components:bankAccountSelect.addBankModal.inputErrors.bankAccountNameCannotBeEmpty',
      );
      setBankAccountNameError({
        hasError: true,
        message: errorMessage,
      });
      return false;
    }
    setBankAccountNameError({ hasError: false, message: '' });
    return true;
  };

  const handleBankAccountNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setBankAccountName(value);

    validateBankAccountName(value);
  };

  const handleConfirmEdit = () => {
    const isValid = validateBankAccountName(bankAccountName);

    if (!isValid) return;

    onEdit(bankAccountName.trim());
    setShowEditBankAccount(false);
  };

  const handleCancelEdit = () => {
    setShowEditBankAccount(false);
    setBankAccountName(bankAccount?.name ?? '');
    setBankAccountNameError({ hasError: false, message: '' });
  };

  const generateIcon = (institutionNumber: string) => {
    const bankInfo = {
      icon: defaultBank,
      name: 'defaultBank',
    };
    switch (institutionNumber) {
      case '001':
        bankInfo.icon = bmoBank;
        bankInfo.name = 'bmoBank';
        break;

      case '002':
        bankInfo.icon = scotiaBank;
        bankInfo.name = 'scotiaBank';
        break;

      case '003':
        bankInfo.icon = rbcBank;
        bankInfo.name = 'rbcBank';
        break;

      case '004':
        bankInfo.icon = tdBank;
        bankInfo.name = 'tdBank';
        break;

      case '006':
        bankInfo.icon = nationalBankOfCanada;
        bankInfo.name = 'nationalBankOfCanada';
        break;

      case '010':
        bankInfo.icon = cibc;
        bankInfo.name = 'cibc';
        break;

      case '016':
        bankInfo.icon = hsbcBank;
        bankInfo.name = 'hsbcBank';
        break;

      case '039':
        bankInfo.icon = LauentianBank;
        bankInfo.name = 'LauentianBank';
        break;

      case '030':
        bankInfo.icon = canadaWesternTrustBank;
        bankInfo.name = 'canadaWesternTrustBank';
        break;

      case '219':
        bankInfo.icon = atbBank;
        bankInfo.name = 'atbBank';
        break;

      case '540':
        bankInfo.icon = manulifeBank;
        bankInfo.name = 'manulifeBank';
        break;

      case '612':
        bankInfo.icon = tangerineBank;
        bankInfo.name = 'tangerineBank';
        break;

      case '623':
        bankInfo.icon = eqLifeBank;
        bankInfo.name = 'eqLifeBank';
        break;

      case '777':
        bankInfo.icon = flinksBank;
        bankInfo.name = 'flinksBank';
        break;

      case '809':
        bankInfo.icon = coastCapitalBank;
        bankInfo.name = 'coastCapitalBank';
        break;

      case '815':
        bankInfo.icon = desjardinsBank;
        bankInfo.name = 'desjardinsBank';
        break;

      case '837':
        bankInfo.icon = meridianBank;
        bankInfo.name = 'meridianBank';
        break;

      default:
        break;
    }
    return bankInfo;
  };

  const allowEdit = options.editBankAccount;
  const allowDelete = options.deleteBankAccount;

  const editButton = (
    <IconButton
      aria-label='edit'
      onClick={() => setShowEditBankAccount(true)}
      size='xsmall'
    >
      <Edit
        data-testid={
          bankAccount?.id ? `${bankAccount.id}-edit-button` : 'edit-button'
        }
        sx={{ fontSize: '16px' }}
        fontSize='inherit'
      />
    </IconButton>
  );

  const deleteButton = (
    <IconButton
      aria-label='delete'
      onClick={() => setShowDeleteBankAccount(true)}
      size='xsmall'
    >
      <Delete
        data-testid={
          bankAccount?.id ? `${bankAccount.id}-remove-button` : 'remove-button'
        }
        sx={{ fontSize: '16px' }}
        fontSize='inherit'
      />
    </IconButton>
  );

  return (
    <>
      <Card>
        <CardContent sx={{ paddingBottom: '16px !important' }}>
          <Grid container spacing={2}>
            <Grid sx={{ display: 'flex', alignItems: 'center' }} size={11}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    borderRadius: '99px',
                    display: 'inline-block',
                    marginRight: '16px',
                  }}
                >
                  <Avatar size='large'>
                    <img
                      alt='advisor'
                      src={generateIcon(bankAccount.institutionNumber).icon}
                      style={{
                        objectFit: 'cover',
                        height: '100%',
                        width: '100%',
                      }}
                    />
                  </Avatar>
                </div>
                <Box>
                  <Typography variant='bodyLarge' sx={{ fontWeight: 800 }}>
                    {bankAccount?.name && <>{bankAccount?.name}&nbsp;</>}
                    {bankAccount.bankAccountNumber}
                  </Typography>
                  <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                    {t(
                      `client:bankAccount.${generateIcon(bankAccount.institutionNumber).name}`,
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid
              sx={{
                display: 'flex',
                justifyContent: 'right',
                alignItems: 'center',
              }}
              size={1}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  top: '-8px',
                }}
              >
                {allowEdit && !allowDelete && editButton}
                {allowDelete && !allowEdit && deleteButton}
                {allowEdit && allowDelete && (
                  <IconButton
                    aria-label='more actions'
                    data-testid={
                      bankAccount?.id
                        ? `${bankAccount.id}-action-button`
                        : 'action-button'
                    }
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setContextMenuAnchorEl(event.currentTarget);
                    }}
                  >
                    <MoreVert fontSize='inherit' />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Context drop-down menu */}
      <Menu
        open={contextMenuAnchorEl !== null}
        anchorEl={contextMenuAnchorEl}
        onClose={() => setContextMenuAnchorEl(null)}
        onClick={() => setContextMenuAnchorEl(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => setShowEditBankAccount(true)}>
          {editButton}
          <Typography variant='labelLarge' sx={{ ml: '0.5em' }}>
            {t('shared:edit')}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => setShowDeleteBankAccount(true)}>
          {deleteButton}
          <Typography variant='labelLarge' sx={{ ml: '0.5em' }}>
            {t('shared:delete')}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={showDeleteBankAccount}
        loading={false}
        title={t('client:bankAccount.deleteBankAccountTitle')}
        bodyText={t('client:bankAccount.deleteBankAccountDesc')}
        onConfirm={async () => {
          onRemove();
          setShowDeleteBankAccount(false);
        }}
        onCancel={() => setShowDeleteBankAccount(false)}
        confirmButtonLabel={t('client:bankAccount.confirm')}
        showCancelButton={true}
      />

      {/* Editing Modal */}
      <ConfirmationModal
        open={showEditBankAccount}
        loading={false}
        title={t('client:bankAccount.editBankAccount')}
        onConfirm={handleConfirmEdit}
        onCancel={handleCancelEdit}
        confirmButtonLabel={t('client:bankAccount.save')}
        showCancelButton={true}
        confirmButtonColor='primary'
        cancelButtonColor='error'
        confirmButtonDisabled={isBankAccountNameEmpty}
      >
        <TextField
          fullWidth
          value={bankAccountName}
          data-testid='bankAccountNameTextField'
          label={t('client:bankAccount.bankAccountName')}
          error={bankAccountNameError.hasError}
          helpText={bankAccountNameError.message}
          onChange={handleBankAccountNameChange}
        />
      </ConfirmationModal>
    </>
  );
};

export default BankAccountCard;
